import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { CollapsiblePanel } from '@ge/components/collapsible-panel';
import { DataLoader } from '@ge/components/data-loader';
import { Text } from '@ge/components/typography';
import useAssetInfoData from '@ge/feat-analyze/data-hooks/use-asset-info-data';
import { DataLoaderType, Capability } from '@ge/models/constants';
import { AuthRender } from '@ge/shared/components/auth-render';
import CaseDamagesTable from '@ge/shared/components/tables/case-damages-table';
import { useComponentHierarchy } from '@ge/shared/data-hooks';
import { globalColors, typography } from '@ge/tokens';

import { TabDetailContainer } from '../../entity-details-shared';
import { AssetComponentHierarchy } from '../asset-component-hierarchy';
import { AssetInfoDetails } from '../asset-info-details';

const AssetContainer = styled.div`
  display: flex;
`;

export const EntityCollapsiblePanel = styled(CollapsiblePanel)`
  margin-bottom: 40px;
  padding-left: 3px;
  width: 98%;

  .title {
    border: 0;
    svg {
      fill: ${(props) => props.theme.entityDetails.cases.details.collapseArrow};
    }
  }
  table {
    border-top: 0 !important;

    thead th {
      border-right: 0 !important;
    }
  }

  table tbody tr:first-of-type td.group-last {
    top: 0px;
  }
  td.group-last {
    background: #2a323b;
    top: 0px !important;
  }
  th.group-last {
    position: sticky !important;
    z-index: 1000 !important;
    span {
      margin-right: 8px;
    }
  }

  .collapsible-panel-content.no-overflow {
    &.expanded {
      max-height: 300px !important;
      transition: max-height 0s ease;
    }
    overflow-y: scroll;
    overflow-x: scroll;
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    &::-webkit-scrollbar-track {
      background: ${(props) => props.theme.scrollbar.trackBackground};
    }
    &::-webkit-scrollbar-thumb {
      background: ${(props) => props.theme.scrollbar.thumbBackground};
      border-radius: 2.5px;
    }
    &::-webkit-scrollbar-thumb:active {
      background: ${(props) => props.theme.scrollbar.thumbActiveBackground};
    }
  }
`;

const StyledAssetInfo = styled.div`
  background: ${(props) => props.theme.entityDetails.sitePanelBlockBackground};
  border-radius: 3px;
  margin: 10px 10px 18px 3px;
  padding: 5px 5px 5px 0px;
  position: relative;
  flex: 1;
  > div {
    margin-bottom: 0px;
    padding-left: 0;
  }
`;

const AssetInfoContainer = styled.div`
  height: 270px;
  width: 700px;
  border-radius: 3px;
  background-color: ${({ theme }) => theme.themeSelector.panelBackgroundColor};
  padding: 10px;
`;

const AssetInfoHeader = styled.div`
  height: 21px;
  width: 75px;
  color: ${({ theme }) => theme.themeSelector.tabsTextColor};
  font-family: ${typography.family.secondary};
  font-size: 18px;
  letter-spacing: 0;
  line-height: 21px;
  padding-top: 16px;
  padding-left: 19px;
  padding-bottom: 20px;
`;

const AssetHeaderBottom = styled.div`
  height: 7px;
  width: 745px;
  background-color: ${globalColors.green1};
  margin-left: 18px;
`;

const HierarchyHeader = styled.div`
  height: 21px;
  width: 97px;
  color: ${({ theme }) => theme.themeSelector.tabsTextColor};
  font-family: ${typography.family.secondary};
  font-size: 18px;
  letter-spacing: 0;
  line-height: 21px;
  padding: 35px 0 10px 29px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const Banner = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin: 110px -55px;
  align-item: center;
  justify-content: center;
  text-align: center;
  span {
    padding-top: 10px;
    margin-top: -5rem;
  }
`;

const NoDataTitle = styled(Text).attrs(() => ({
  type: typography.textTypes.body,
  level: 1,
}))`
  margin-bottom: 6px;
`;

const getChildComponent = (componentData) => {
  let level2Children = [];
  let level3Children = [];

  componentData.map((component) => {
    component.children.map((data) => {
      level2Children.push(data);
      return data['children'].map((child) => level3Children.push(child));
    });
    return level2Children;
  });
  return { level2Children, level3Children };
};

export const AssetInfo = ({
  entity,
  isMonitor,
  assetDamages,
  isLoading,
  sortAction,
  sortMetric,
  sortDirection,
  onFilter,
  onFilterChange,
  filterValues,
  handleSelectDamage,
  isAuthorizedToViewAssetDamage,
}) => {
  const { t, ready } = useTranslation(['entity-details'], {
    useSuspense: false,
  });

  const [level2Component, setlevel2Component] = useState();
  const [level3Component, setlevel3Component] = useState();

  const { data, isLoading: assetInfoLoading } = useAssetInfoData(entity.id, isMonitor);
  const { data: componentHierarchy, isLoading: componentHierarchyLoading } = useComponentHierarchy([
    entity.id,
  ]);

  const { level2Children, level3Children } = useMemo(
    () => (componentHierarchy ? getChildComponent(componentHierarchy) : []),
    [componentHierarchy],
  );

  useEffect(() => {
    if (level2Children) setlevel2Component(level2Children);
    if (level3Children) setlevel3Component(level3Children);
  }, [level2Children, level3Children]);

  const handleComponentClick = useCallback((data, level) => {
    if (level === 'level1') {
      setlevel2Component(data.children);
      setlevel3Component();
    }
    if (level === 'level2') {
      setlevel3Component(data.children);
    }
  }, []);

  if (!ready) {
    return null;
  }

  return (
    <TabDetailContainer>
      <AssetContainer>
        <StyledAssetInfo>
          <AssetInfoContainer>
            <AssetInfoHeader>{t('asset_panel.asset_info', 'Asset Info')}</AssetInfoHeader>
            <AssetHeaderBottom />
            <DataLoader
              isLoading={assetInfoLoading}
              type={DataLoaderType.TABLE}
              renderCondition={true}
              margin="10px"
            >
              <AssetInfoDetails entity={entity} entityData={data} />
            </DataLoader>
          </AssetInfoContainer>
        </StyledAssetInfo>
      </AssetContainer>
      {isAuthorizedToViewAssetDamage && (
        <AuthRender capability={Capability.INTERNAL_ACCESS} view siteLevel={false}>
          <EntityCollapsiblePanel
            headerContent={
              <HeaderContent>
                <h2>{t('asset_panel.conditions', 'Conditions')}</h2>
              </HeaderContent>
            }
            expanded={true}
          >
            <DataLoader type={DataLoaderType.TABLE} isLoading={isLoading}>
              <CaseDamagesTable
                caseDamages={assetDamages}
                sortAction={sortAction}
                sortMetric={sortMetric}
                sortDirection={sortDirection}
                onFilter={onFilter}
                onFilterChange={onFilterChange}
                filterValues={filterValues}
                isAssetPanel={true}
                handleSelectDamage={handleSelectDamage}
              />
            </DataLoader>
            {assetDamages?.length === 0 && (
              <Banner>
                <span>
                  <NoDataTitle>{t('asset_panel.no_asset_damage', 'No Damages')}</NoDataTitle>
                </span>
              </Banner>
            )}
          </EntityCollapsiblePanel>
        </AuthRender>
      )}


      <HierarchyHeader>{t('asset_panel.components', 'Components')}</HierarchyHeader>
      <DataLoader
        isLoading={componentHierarchyLoading}
        type={DataLoaderType.TABLE}
        renderCondition={true}
        margin="10px"
      >
        <AssetComponentHierarchy
          onComponentSelect={handleComponentClick}
          componentList={data?.componentList}
          componentHierarchy={componentHierarchy}
          level2Component={level2Component}
          level3Component={level3Component}
        />
      </DataLoader>
    </TabDetailContainer>
  );
};

AssetInfo.propTypes = {
  entity: PropTypes.instanceOf(Object),
  isMonitor: PropTypes.bool,
  assetDamages: PropTypes.any,
  isLoading: PropTypes.bool,
  sortAction: () => null,
  sortMetric: '',
  sortDirection: '',
  onFilter: () => null,
  onFilterChange: () => null,
  filterValues: {},
  handleSelectDamage: () => null,
  isAuthorizedToViewAssetDamage: PropTypes.bool,
};
